// ─────────────────────────────────────────────────────────────────────────────
// Biblioteca de animações (MotionGraphic) — SCHEMA PURO, sem React nem imports.
//
// CÓPIA IDÊNTICA em backend/src/services/videoEdit/graphicsSchema.ts (o backend não
// importa TSX do frontend). Tudo abaixo da linha "BEGIN SHARED" tem de ser igual nos
// dois arquivos — backend/tests/video-edit-graphics.test.ts confere. Mude os dois juntos.
//
// No edvid o Claude escreve um CustomGraphics.tsx sob medida por vídeo. Um SaaS não roda
// código escrito por LLM, então a IA escolhe template + props + tempo desta biblioteca e
// o validateGraphic() limpa/limita tudo antes de chegar à composição (planner no backend e
// inspetor da aba Final usam a MESMA função).
// ─────────────────────────────────────────────────────────────────────────────
// BEGIN SHARED

export const GRAPHIC_TEMPLATES = [
  'counter',
  'checklist',
  'typewriter',
  'timeline',
  'arrow_callout',
  'compare_bars',
  'keyword_pop',
  'steps',
] as const;

export type GraphicTemplateId = (typeof GRAPHIC_TEMPLATES)[number];
export type GraphicZone = 'top' | 'middle';

/** Forma estrutural de MotionGraphic (contract.ts) — repetida aqui para o schema não importar nada. */
export interface GraphicLike {
  id: string;
  template: GraphicTemplateId;
  start: number;
  end: number;
  zone: GraphicZone;
  props: Record<string, unknown>;
}

// ─── Props por template (o que a composição recebe depois do validateGraphic) ───
export interface CounterProps { value: number; prefix?: string; suffix?: string; label?: string; decimals?: number; accent?: string }
export interface ChecklistProps { items: string[]; title?: string; accent?: string }
export interface TypewriterProps { text: string; title?: string; accent?: string }
export interface TimelineProps { steps: string[]; accent?: string }
export type ArrowDirection = 'up' | 'down' | 'left' | 'right';
/** x,y = ALVO da seta (0–1 do quadro); direction = para onde a seta aponta (o rótulo fica do lado oposto) */
export interface ArrowCalloutProps { label: string; x: number; y: number; direction: ArrowDirection; accent?: string }
export interface CompareBar { label: string; value: number }
export interface CompareBarsProps { left: CompareBar; right: CompareBar; unit?: string; title?: string; accent?: string }
export interface KeywordPopProps { word: string; sub?: string; accent?: string }
export interface StepsProps { items: string[]; accent?: string }

export interface GraphicPropsMap {
  counter: CounterProps;
  checklist: ChecklistProps;
  typewriter: TypewriterProps;
  timeline: TimelineProps;
  arrow_callout: ArrowCalloutProps;
  compare_bars: CompareBarsProps;
  keyword_pop: KeywordPopProps;
  steps: StepsProps;
}

// ─── Descrição dos campos (inspetor da aba Final e prompt do planner) ───
export type GraphicField =
  | { key: string; label: string; kind: 'text'; maxLen: number; required?: boolean; placeholder?: string }
  | { key: string; label: string; kind: 'number'; min: number; max: number; step?: number; required?: boolean; integer?: boolean }
  | { key: string; label: string; kind: 'list'; maxItems: number; minItems: number; maxLen: number }
  | { key: string; label: string; kind: 'select'; options: Array<{ value: string; label: string }> }
  | { key: string; label: string; kind: 'fraction'; min: number; max: number }
  | { key: string; label: string; kind: 'bar'; maxLen: number };

export interface GraphicTemplateSpec {
  id: GraphicTemplateId;
  /** nome na UI (pt-BR) */
  name: string;
  /** o que é, em uma linha */
  description: string;
  /** quando a IA deve escolher (vai no prompt) */
  when: string;
  fields: GraphicField[];
  /** props de exemplo — "+ adicionar gráfico" e troca de template partem daqui */
  defaults: Record<string, unknown>;
  defaultZone: GraphicZone;
  /** o template se posiciona sozinho (seta aponta um alvo x,y) — a zona é ignorada */
  selfPositioned?: boolean;
  /** duração recomendada (s) */
  minDur: number;
  maxDur: number;
}

export const GRAPHIC_MIN_DUR = 1.2;
export const GRAPHIC_MAX_DUR = 3.5;
/** teto absoluto aceito pelo validador (o usuário pode esticar no editor além do recomendado) */
export const GRAPHIC_HARD_MAX_DUR = 12;
export const GRAPHIC_MAX_COUNT = 6;

const ACCENT_FIELD: GraphicField = { key: 'accent', label: 'Cor (vazio = destaque do vídeo)', kind: 'text', maxLen: 7, placeholder: '#ff5200' };

export const GRAPHIC_SPECS: Record<GraphicTemplateId, GraphicTemplateSpec> = {
  counter: {
    id: 'counter',
    name: 'Contador',
    description: 'Número grande que conta até o valor, com rótulo embaixo.',
    when: 'a fala diz um número que importa (preço, porcentagem, multiplicador, tempo, quantidade)',
    fields: [
      { key: 'value', label: 'Valor', kind: 'number', min: -1e9, max: 1e9, step: 1, required: true },
      { key: 'prefix', label: 'Antes do número', kind: 'text', maxLen: 6, placeholder: 'R$' },
      { key: 'suffix', label: 'Depois do número', kind: 'text', maxLen: 8, placeholder: '%' },
      { key: 'label', label: 'Rótulo', kind: 'text', maxLen: 40, placeholder: 'de lucro por peça' },
      { key: 'decimals', label: 'Casas decimais', kind: 'number', min: 0, max: 2, step: 1, integer: true },
      ACCENT_FIELD,
    ],
    defaults: { value: 87, suffix: '%', label: 'dos clientes voltam', decimals: 0 },
    defaultZone: 'top',
    minDur: 1.4,
    maxDur: 3.2,
  },
  checklist: {
    id: 'checklist',
    name: 'Checklist',
    description: 'Itens que entram um a um e ganham um ✓ desenhado.',
    when: 'a fala enumera requisitos, benefícios ou coisas a conferir (2 a 4 itens curtos)',
    fields: [
      { key: 'title', label: 'Título (opcional)', kind: 'text', maxLen: 28 },
      { key: 'items', label: 'Itens', kind: 'list', minItems: 1, maxItems: 4, maxLen: 30 },
      ACCENT_FIELD,
    ],
    defaults: { items: ['Custo do material', 'Tempo de máquina', 'Taxa do marketplace'] },
    defaultZone: 'top',
    minDur: 1.8,
    maxDur: 3.5,
  },
  typewriter: {
    id: 'typewriter',
    name: 'Máquina de escrever',
    description: 'Folha de papel com a frase sendo digitada e um cursor.',
    when: 'a fala cita um roteiro, uma mensagem, uma frase exata, uma pergunta ou uma busca',
    fields: [
      { key: 'title', label: 'Título (opcional)', kind: 'text', maxLen: 20, placeholder: 'ROTEIRO' },
      { key: 'text', label: 'Frase', kind: 'text', maxLen: 90, required: true },
      ACCENT_FIELD,
    ],
    defaults: { title: 'ROTEIRO', text: 'Quanto custa, de verdade, cada peça que você vende?' },
    defaultZone: 'top',
    minDur: 1.8,
    maxDur: 3.5,
  },
  timeline: {
    id: 'timeline',
    name: 'Linha do tempo',
    description: 'Etapas numa linha; um marcador anda de uma para a outra.',
    when: 'a fala descreve um processo ou uma evolução em etapas (antes → durante → depois; 2 a 4 etapas)',
    fields: [
      { key: 'steps', label: 'Etapas', kind: 'list', minItems: 2, maxItems: 4, maxLen: 16 },
      ACCENT_FIELD,
    ],
    defaults: { steps: ['Corte', 'Legenda', 'Animação'] },
    defaultZone: 'top',
    minDur: 1.8,
    maxDur: 3.5,
  },
  arrow_callout: {
    id: 'arrow_callout',
    name: 'Seta com rótulo',
    description: 'Seta desenhada a lápis apontando um ponto do quadro, com rótulo.',
    when: 'a fala aponta algo na tela ("olha aqui", "isso aqui", "esse produto") — sem saber a posição, use x 0.3, y 0.64, direção "left" (rótulo à direita, na altura do peito, longe do rosto)',
    fields: [
      { key: 'label', label: 'Rótulo', kind: 'text', maxLen: 24, required: true },
      { key: 'x', label: 'Alvo · horizontal', kind: 'fraction', min: 0.08, max: 0.92 },
      { key: 'y', label: 'Alvo · vertical', kind: 'fraction', min: 0.12, max: 0.85 },
      {
        key: 'direction',
        label: 'A seta aponta para',
        kind: 'select',
        options: [
          { value: 'down', label: 'baixo' },
          { value: 'up', label: 'cima' },
          { value: 'left', label: 'esquerda' },
          { value: 'right', label: 'direita' },
        ],
      },
      ACCENT_FIELD,
    ],
    defaults: { label: 'Olha isso', x: 0.3, y: 0.64, direction: 'left' },
    defaultZone: 'middle',
    selfPositioned: true,
    minDur: 1.2,
    maxDur: 3,
  },
  compare_bars: {
    id: 'compare_bars',
    name: 'Comparação em barras',
    description: 'Duas barras que crescem lado a lado (antes × depois, nós × eles).',
    when: 'a fala compara duas quantidades (antes/depois, com/sem, nós/concorrente)',
    fields: [
      { key: 'title', label: 'Título (opcional)', kind: 'text', maxLen: 28 },
      { key: 'left', label: 'Barra da esquerda', kind: 'bar', maxLen: 16 },
      { key: 'right', label: 'Barra da direita (destaque)', kind: 'bar', maxLen: 16 },
      { key: 'unit', label: 'Unidade', kind: 'text', maxLen: 6, placeholder: '%' },
      ACCENT_FIELD,
    ],
    defaults: { left: { label: 'Antes', value: 12 }, right: { label: 'Depois', value: 38 }, unit: '%' },
    defaultZone: 'top',
    minDur: 1.8,
    maxDur: 3.5,
  },
  keyword_pop: {
    id: 'keyword_pop',
    name: 'Palavra-chave',
    description: 'Uma palavra grande que salta com marca-texto e sublinhado a lápis.',
    when: 'a fala enfatiza uma afirmação ou conceito central numa palavra só (lucro, prejuízo, grátis, erro)',
    fields: [
      { key: 'word', label: 'Palavra', kind: 'text', maxLen: 18, required: true },
      { key: 'sub', label: 'Linha de apoio (opcional)', kind: 'text', maxLen: 36 },
      ACCENT_FIELD,
    ],
    defaults: { word: 'LUCRO', sub: 'ou prejuízo?' },
    defaultZone: 'top',
    minDur: 1.2,
    maxDur: 2.8,
  },
  steps: {
    id: 'steps',
    name: 'Passos 1-2-3',
    description: 'Passos numerados que entram em sequência.',
    when: 'a fala dá instruções em ordem ("primeiro… depois… por fim"; até 3 passos)',
    fields: [
      { key: 'items', label: 'Passos', kind: 'list', minItems: 1, maxItems: 3, maxLen: 34 },
      ACCENT_FIELD,
    ],
    defaults: { items: ['Some o custo do material', 'Inclua a taxa da plataforma', 'Defina a margem'] },
    defaultZone: 'top',
    minDur: 1.8,
    maxDur: 3.5,
  },
};

export const isGraphicTemplate = (v: unknown): v is GraphicTemplateId =>
  typeof v === 'string' && (GRAPHIC_TEMPLATES as readonly string[]).includes(v);

// ─── Sanitização ───
const clampNum = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Espaços colapsados, sem quebras/controle; corta no limite (na palavra, com "…" se couber). */
export function cleanGraphicText(v: unknown, maxLen: number): string {
  if (v === null || v === undefined || typeof v === 'object') return '';
  let s = String(v)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.length <= maxLen) return s;
  const cut = s.slice(0, maxLen);
  const sp = cut.lastIndexOf(' ');
  s = sp >= maxLen * 0.6 ? cut.slice(0, sp) : cut;
  return s.replace(/[\s,;:.\-–—]+$/, '');
}

/** "12,5" / "R$ 2.000" / "3x" → número; NaN se não houver número. */
export function parseGraphicNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v !== 'string') return NaN;
  let s = v.replace(/[^\d.,-]/g, '');
  if (!s) return NaN;
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export function normalizeGraphicHex(v: unknown): string | undefined {
  let s = String(v ?? '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(s)) s = s.split('').map((c) => c + c).join('');
  return /^[0-9a-f]{6}$/i.test(s) ? `#${s.toLowerCase()}` : undefined;
}

function cleanList(v: unknown, maxItems: number, maxLen: number): string[] {
  const arr = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/\n|;|\|/) : [];
  return arr.map((x) => cleanGraphicText(x, maxLen)).filter(Boolean).slice(0, maxItems);
}

export interface SanitizeResult<T = Record<string, unknown>> {
  props: T | null;
  /** o que foi ajustado (pt-BR) — o planner transforma em aviso, o inspetor ignora */
  fixes: string[];
  /** por que ficou inválido (props=null) */
  error?: string;
}

/**
 * Limpa e limita as props de um template. Nunca lança. props=null quando falta o essencial
 * (ex.: contador sem número, linha do tempo com 1 etapa, palavra-chave vazia).
 */
export function sanitizeGraphicProps(template: GraphicTemplateId, raw: unknown): SanitizeResult {
  const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  const fixes: string[] = [];
  const out: Record<string, unknown> = {};
  const accent = normalizeGraphicHex(p.accent);
  if (accent) out.accent = accent;
  const text = (key: string, maxLen: number) => {
    const before = p[key] === undefined || p[key] === null ? '' : String(p[key]).replace(/\s+/g, ' ').trim();
    const s = cleanGraphicText(p[key], maxLen);
    if (before.length > maxLen) fixes.push(`${key} cortado em ${maxLen} caracteres`);
    return s;
  };
  const list = (key: string, maxItems: number, maxLen: number) => {
    const rawLen = Array.isArray(p[key]) ? p[key].length : 0;
    const items = cleanList(p[key], maxItems, maxLen);
    if (rawLen > maxItems) fixes.push(`${key}: só os ${maxItems} primeiros`);
    return items;
  };

  switch (template) {
    case 'counter': {
      const value = parseGraphicNumber(p.value);
      if (!Number.isFinite(value)) return { props: null, fixes, error: 'contador sem número' };
      out.value = clampNum(value, -1e9, 1e9);
      const decimals = Number.isFinite(Number(p.decimals))
        ? clampNum(Math.round(Number(p.decimals)), 0, 2)
        : Number.isInteger(value) ? 0 : Math.min(2, String(value).split('.')[1]?.length || 0);
      out.decimals = decimals;
      const prefix = text('prefix', 6);
      const suffix = text('suffix', 8);
      const label = text('label', 40);
      if (prefix) out.prefix = prefix;
      if (suffix) out.suffix = suffix;
      if (label) out.label = label;
      break;
    }
    case 'checklist': {
      const items = list('items', 4, 30);
      if (!items.length) return { props: null, fixes, error: 'checklist sem itens' };
      out.items = items;
      const title = text('title', 28);
      if (title) out.title = title;
      break;
    }
    case 'typewriter': {
      const t = text('text', 90);
      if (!t) return { props: null, fixes, error: 'máquina de escrever sem frase' };
      out.text = t;
      const title = text('title', 20);
      if (title) out.title = title;
      break;
    }
    case 'timeline': {
      const steps = list('steps', 4, 16);
      if (steps.length < 2) return { props: null, fixes, error: 'linha do tempo precisa de 2 a 4 etapas' };
      out.steps = steps;
      break;
    }
    case 'arrow_callout': {
      const label = text('label', 24);
      if (!label) return { props: null, fixes, error: 'seta sem rótulo' };
      out.label = label;
      const fx = Number(p.x);
      const fy = Number(p.y);
      out.x = Number.isFinite(fx) ? clampNum(fx, 0.08, 0.92) : 0.3;
      out.y = Number.isFinite(fy) ? clampNum(fy, 0.12, 0.85) : 0.64;
      if ((Number.isFinite(fx) && fx !== out.x) || (Number.isFinite(fy) && fy !== out.y)) fixes.push('alvo da seta trazido para dentro da área segura');
      const dir = String(p.direction || '').toLowerCase();
      out.direction = (['up', 'down', 'left', 'right'] as const).includes(dir as ArrowDirection) ? dir : 'left';
      break;
    }
    case 'compare_bars': {
      const bar = (b: any, fallback: string): CompareBar | null => {
        if (!b || typeof b !== 'object') return null;
        const value = parseGraphicNumber(b.value);
        if (!Number.isFinite(value)) return null;
        return { label: cleanGraphicText(b.label, 16) || fallback, value: clampNum(Math.abs(value), 0, 1e9) };
      };
      const left = bar(p.left, 'Antes');
      const right = bar(p.right, 'Depois');
      if (!left || !right) return { props: null, fixes, error: 'comparação precisa de dois valores' };
      if (left.value === 0 && right.value === 0) return { props: null, fixes, error: 'comparação com os dois valores zerados' };
      out.left = left;
      out.right = right;
      const unit = text('unit', 6);
      if (unit) out.unit = unit;
      const title = text('title', 28);
      if (title) out.title = title;
      break;
    }
    case 'keyword_pop': {
      const word = text('word', 18);
      if (!word) return { props: null, fixes, error: 'palavra-chave vazia' };
      out.word = word;
      const sub = text('sub', 36);
      if (sub) out.sub = sub;
      break;
    }
    case 'steps': {
      const items = list('items', 3, 34);
      if (!items.length) return { props: null, fixes, error: 'passos sem itens' };
      out.items = items;
      break;
    }
    default:
      return { props: null, fixes, error: 'template desconhecido' };
  }
  return { props: out, fixes };
}

export interface ValidateGraphicOptions {
  /** duração do vídeo (s) — o gráfico é recortado para caber */
  durationSec?: number;
  /** id usado quando o gráfico não trouxer um */
  fallbackId?: string;
}

export interface ValidateGraphicResult {
  graphic: GraphicLike | null;
  warnings: string[];
}

/**
 * Valida um MotionGraphic inteiro: template conhecido, props limpas, tempos finitos e
 * ordenados (≥0,3s, ≤12s, dentro do vídeo), zona top|middle. Nunca lança.
 */
export function validateGraphic(g: unknown, opts: ValidateGraphicOptions = {}): ValidateGraphicResult {
  const warnings: string[] = [];
  if (!g || typeof g !== 'object') return { graphic: null, warnings: ['gráfico vazio'] };
  const r = g as Record<string, any>;
  const template = String(r.template || '').trim().toLowerCase();
  if (!isGraphicTemplate(template)) return { graphic: null, warnings: [`template desconhecido: ${String(r.template ?? '').slice(0, 40)}`] };
  const spec = GRAPHIC_SPECS[template];
  const s = sanitizeGraphicProps(template, r.props);
  if (!s.props) return { graphic: null, warnings: [`${spec.name}: ${s.error || 'props inválidas'}`] };
  for (const f of s.fixes) warnings.push(`${spec.name}: ${f}`);

  let start = Number(r.start);
  let end = Number(r.end);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { graphic: null, warnings: [...warnings, `${spec.name}: tempo inválido`] };
  if (end < start) [start, end] = [end, start];
  start = Math.max(0, start);
  const total = Number(opts.durationSec);
  if (Number.isFinite(total) && total > 0) {
    if (start >= total - 0.3) return { graphic: null, warnings: [...warnings, `${spec.name}: começa depois do fim do vídeo`] };
    end = Math.min(end, total);
  }
  if (end - start > GRAPHIC_HARD_MAX_DUR) end = start + GRAPHIC_HARD_MAX_DUR;
  if (end - start < 0.3) end = start + 0.3;
  if (Number.isFinite(total) && total > 0 && end > total) {
    end = total;
    start = Math.max(0, end - 0.3);
  }
  const zone: GraphicZone = r.zone === 'middle' || r.zone === 'top' ? r.zone : spec.defaultZone;
  const id = cleanGraphicText(r.id, 40).replace(/[^\w-]/g, '') || opts.fallbackId || `g${Math.round(start * 1000)}`;
  return {
    graphic: { id, template, start: Math.round(start * 1000) / 1000, end: Math.round(end * 1000) / 1000, zone, props: s.props },
    warnings,
  };
}

/** Troca o template de um gráfico levando os textos que fazem sentido (inspetor). */
export function convertGraphicProps(from: Record<string, unknown>, to: GraphicTemplateId): Record<string, unknown> {
  const base: Record<string, unknown> = { ...GRAPHIC_SPECS[to].defaults };
  const texts: string[] = [];
  const push = (v: unknown) => { if (typeof v === 'string' && v.trim()) texts.push(v.trim()); };
  for (const k of ['word', 'label', 'text', 'title', 'sub']) push(from[k]);
  for (const k of ['items', 'steps']) if (Array.isArray(from[k])) (from[k] as unknown[]).forEach(push);
  const list = Array.isArray(from.items) ? (from.items as string[]) : Array.isArray(from.steps) ? (from.steps as string[]) : null;
  const num = typeof from.value === 'number' ? from.value : undefined;
  switch (to) {
    case 'counter': if (num !== undefined) { base.value = num; base.prefix = from.prefix; base.suffix = from.suffix; } if (typeof from.label === 'string') base.label = from.label; break;
    case 'checklist': if (list?.length || texts.length) base.items = (list?.length ? list : texts).slice(0, 4); break;
    case 'steps': if (list?.length || texts.length) base.items = (list?.length ? list : texts).slice(0, 3); break;
    case 'timeline': if (list && list.length >= 2) base.steps = list.slice(0, 4); break;
    case 'typewriter': if (texts[0]) base.text = texts.join(' — ').slice(0, 90); break;
    case 'keyword_pop': if (texts[0]) base.word = texts[0].split(' ').slice(0, 2).join(' '); break;
    case 'arrow_callout': if (texts[0]) base.label = texts[0]; break;
    default: break;
  }
  if (from.accent) base.accent = from.accent;
  const s = sanitizeGraphicProps(to, base);
  return s.props || { ...GRAPHIC_SPECS[to].defaults };
}

/** Texto curto para o chip da timeline. */
export function graphicSummary(g: Pick<GraphicLike, 'template' | 'props'>): string {
  const p = g.props || {};
  const name = isGraphicTemplate(g.template) ? GRAPHIC_SPECS[g.template].name : String(g.template);
  let detail = '';
  switch (g.template) {
    case 'counter': detail = `${p.prefix || ''}${p.value ?? ''}${p.suffix || ''}`; break;
    case 'checklist': case 'steps': detail = Array.isArray(p.items) ? (p.items as string[]).join(' · ') : ''; break;
    case 'timeline': detail = Array.isArray(p.steps) ? (p.steps as string[]).join(' → ') : ''; break;
    case 'typewriter': detail = String(p.text || ''); break;
    case 'arrow_callout': detail = String(p.label || ''); break;
    case 'compare_bars': detail = `${(p.left as any)?.label ?? ''} × ${(p.right as any)?.label ?? ''}`; break;
    case 'keyword_pop': detail = String(p.word || ''); break;
    default: break;
  }
  return detail ? `${name} · ${detail}` : name;
}

/** Número no formato pt-BR (1.234,5). */
export function formatGraphicNumber(v: number, decimals = 0): string {
  const d = clampNum(Math.round(decimals), 0, 2);
  const neg = v < 0;
  const fixed = Math.abs(v).toFixed(d);
  const [int, frac] = fixed.split('.');
  const withDots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${neg ? '-' : ''}${withDots}${frac ? `,${frac}` : ''}`;
}
